import React, { useState, useEffect, useRef } from "react";
import { ActivityItem, DefaultButton, IActivityItemProps, PrimaryButton } from "office-ui-fabric-react";
import { commentsApi } from "../../api/commentApi";
import { Comment, TruckComment } from "../../models";
import { mergeStyleSets, mergeStyles } from "@fluentui/react";
import { MessageBar, MessageBarType } from "@fluentui/react";
import { Persona, PersonaSize } from "office-ui-fabric-react";
import { pushNotification } from "../../api/pushNotification";
import { TextField, IconButton, Image } from "@fluentui/react";
import ApiSpinner from "../common/ApiSpinner";
import clsx from "clsx";
import Moment from "react-moment";
import SofIcon from "../common/Icons";
import moment from "moment";

const panel = mergeStyles({
  display: "flex"
});
const panelCol = mergeStyles([
  panel,
  {
    flexDirection: "column"
  }
]);
const panelRow = mergeStyles([
  panel,
  {
    flexDirection: "row"
  }
]);
const buttonBase = mergeStyles({
  padding: "9px 10px",
  fontSize: 16,
  fontWeight: 500,
  height: 42,
  border: "none",
  color: "#4c4c4c",
  selectors: {
    [":active"]: {
      backgroundColor: "#008cc0"
    }
  }
});
const classes = mergeStyleSets({
  outerContainer: [
    panelCol,
    {
      flex: "1 1 auto",
      height: "100%"
      // height: "calc(100% - 115px)",
    }
  ],
  headerPanel: [
    panel,
    {
      alignItems: "center",
      // border: "1px solid #E2E6E8",
      height: 40,
      padding: "8px 16px",
      background: "#EEF0F8"
    }
  ],
  headerTitle: {
    flexGrow: 1,
    color: "#202323",
    fontWeight: "bold",
    fontSize: "14px",
    lineHeight: "20px"
  },
  contentPanel: {
    flex: "1 1 auto",
    border: "1px solid #E2E6E8",
    overflowY: "auto",
    height: 0,
    padding: "10px"
  },
  footerPanel: [
    panelRow,
    {
      border: "1px solid #D8DFE6",
      borderRadius: 2,
      marginTop: 10,
      padding: 3
    }
  ],
  inlineBlockDiv: {
    display: "inline-block",
    paddingRight: "10px",
    width: "40px"
  },
  bottomMarginDiv: {
    marginBottom: "4px"
  },
  emptyBigDiv: {
    color: "#4C4C4C",
    fontSize: "18px",
    fontWeight: "bold",
    margin: "10px 0"
  },
  emptyMedDiv: {
    color: "#7C7777",
    fontSize: "14px",
    maxWidth: 300,
    margin: "auto"
  },
  roundBgDiv: {
    width: "74px",
    height: "74px",
    borderRadius: "35px",
    backgroundColor: "#EEF0F8",
    display: "block",
    padding: "22px"
  },
  backButtonSpan: {
    paddingLeft: "5px",
    paddingRight: "5px"
  },
  zeroCommentDiv: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    textAlign: "center",
    flexDirection: "column",
    marginTop: "30px"
  },
  iconStyles: {
    color: "#4C4C4C",
    height: 32,
    width: 32
  },
  commentTextDiv: {
    whiteSpace: "pre-wrap"
  },
  header: {
    fontSize: 20,
    fontWeight: 500,
    lineHeight: 24,
    color: "#00447E",
    textAlign: "left"
  },
  body: {
    textAlign: "left",
    margin: "18px 0 38px 0"
  },
  footer: {
    textAlign: "right"
  },
  button: [buttonBase, {}],
  primaryButton: [
    buttonBase,
    {
      backgroundColor: "#009FDF",
      color: "#FFFFFF",
      padding: "9px 20px"
    }
  ],
  modalButton: [
    buttonBase,
    {
      padding: "9px 20px"
    }
  ]
});

interface IProps {
  nominationNo: string | undefined;
  isAddCommentAllowed: boolean;
  isDeleteCommentAllowed: boolean;
  isOffline: boolean;
  readonly: boolean;
  onClose(): void;
}

const getActionTimeString = (date: Date): string => date.toISOString();

const useAsync = function <T>(
  asyncFn: () => Promise<T>,
  success: (result: T) => void
): void {
  useEffect(() => {
    let isMounted = true;
    asyncFn().then((data) => {
      if (isMounted) success(data);
    });
    return () => {
      isMounted = false;
    };
  }, [asyncFn, success]);
};

const RemarksPane: React.FC<IProps> = (props: IProps) => {
  const [comments, setComments] = useState<TruckComment[]>([]);
  const [commentText, setCommentText] = useState<string>("");
  const [commentId, setCommentId] = useState<number>(0);
  const commentEnd = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const getComments = async () => {
    if (props.nominationNo) {
      const comments = await commentsApi.getTruckCommentsAsync(
        props.nominationNo
      );
      setComments(comments);
    }
  };

  const onSubmit = async () => {
    try {
      await commentsApi.addTruckCommentAsync({
        commentText,
        nominationNo: props.nominationNo,
        submittedOn: getActionTimeString(new Date())
      } as TruckComment);
      await getComments();
      setCommentText("");
      setErrorMessage("");
    } catch (e) {
      console.log(e);
      setErrorMessage("Unable to save changes. Please try to reconnect.");
    }
  };

  const deleteCommentClick = (commentId: number) => {
    setShowModal(true);
    setCommentId(commentId);
  };

  const deleteComment = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    if (props.nominationNo) {
      try {
        setIsLoading(true);
        await commentsApi.deleteTruckCommentAsync(
          props.nominationNo,
          commentId
        );
        await getComments();
        setErrorMessage("");
        setShowModal(false);
        setIsLoading(false);
      } catch (err) {
        setShowModal(false);
        setIsLoading(false);
        setErrorMessage("Unable to save changes. Please try to reconnect.");
      }
    }
  };

  const commentActivities = comments.map(
    (c, index: number) =>
      ({
        key: index,
        activityDescription: [
          <table key={index}>
            <tr>
              <td rowSpan={2} style={{ verticalAlign: "top" }}>
                <span className={classes.inlineBlockDiv}>
                  {c.profilePicture && (
                    <Image
                      style={{
                        borderRadius: "50%",
                        height: "auto"
                      }}
                      src={`data:image/jpeg;base64, ${c.profilePicture}`}
                      width="30"
                      height="30"
                    />
                  )}
                  {!c.profilePicture && (
                    <Persona
                      size={PersonaSize.size28}
                      initialsColor={"#4C4C4C"}
                      color={"#4C4C4C"}
                      imageInitials={c.submittedByName
                        .split(" (")[0]
                        .split(" ")
                        .map((n, i, a) =>
                          i === 0 || i + 1 === a.length ? n[0] : null
                        )
                        .join("")}
                    />
                  )}
                </span>
              </td>
              <td>
                <b key={`submittedby-${index}`}>{c.submittedByName}</b>&nbsp;
                <Moment format="HH:mm - ll">
                  {moment.utc(c.submittedOn).local().toDate()}
                </Moment>
              </td>
              {props.isDeleteCommentAllowed && !props.readonly && (
                <IconButton
                  iconProps={{
                    iconName: "Delete",
                    className: classes.iconStyles
                  }}
                  onClick={() => deleteCommentClick(c.commentId)}
                />
              )}
            </tr>

            <tr>
              <td>
                <div className={classes.commentTextDiv}>{c.commentText}</div>
              </td>
            </tr>
          </table>
        ]
      } as IActivityItemProps | { key: number })
  );

  useEffect(() => {
    let isMounted = false;

    if (props.isOffline) {
      //setErrorMessage("You are offline. Comments cannot be processed.");
    } else {
      getComments();
      setErrorMessage("");
    }

    return () => {
      isMounted = false;
    };
  }, [props.isOffline]);

  useEffect(() => {
    setTimeout(() => {
      if (commentEnd.current)
        commentEnd.current.scrollIntoView({
          behavior: "smooth"
        });
    }, 100);
  }, [comments]);

  useEffect(() => {
    pushNotification.connection.off("CommentAdded");

    pushNotification.connection.on("CommentAdded", (message) => {
      var comment = message as Comment;

      if (comment.nominationNo === props.nominationNo) {
        getComments();
      }
    });

    return () => pushNotification.connection.off("CommentAdded");
  }, []);

  return (
    <>
      <div className={classes.outerContainer}>
        <div className={classes.headerPanel}>
          <span className={classes.headerTitle}>
            {props.nominationNo === "0"
              ? "Line Handling Comments"
              : "Nomination Comments"}
          </span>
        </div>
        <div className={clsx(classes.contentPanel, "custom-scroll")}>
          {isLoading && <ApiSpinner />}
          {!isLoading && commentActivities.length === 0 && (
            <div className={classes.zeroCommentDiv}>
              <div className={classes.roundBgDiv}>
                <SofIcon iconname="NoComment" />
              </div>
              <div className={classes.emptyBigDiv}>No Comments Yet</div>
              <div className={classes.emptyMedDiv}>
                Use the text area below to add comments associated to&nbsp;
                Nomination No{" "}
                {props.nominationNo &&
                  props.nominationNo.substr(props.nominationNo.length - 6)}
              </div>
            </div>
          )}
          {!isLoading &&
            commentActivities.map((a, index: number) => (
              <ActivityItem
                key={`comment-${index}`}
                {...a}
                className={classes.bottomMarginDiv}
              />
            ))}
          <div id="comments-bottom" ref={commentEnd}></div>
        </div>
        {props.isAddCommentAllowed && (
          <div className={classes.footerPanel}>
            <TextField
              id="txt-comment"
              placeholder="Add comment"
              rows={1}
              multiline
              maxLength={500}
              value={commentText}
              disabled={props.readonly || isLoading}
              resizable={false}
              styles={{
                field: { lineHeight: "42px" },
                fieldGroup: { border: "none", minHeight: "auto" },
                root: { flexGrow: 1 }
              }}
              onChange={(e, newValue?: string | undefined) =>
                setCommentText(newValue === undefined ? "" : newValue)
              }
            />
            <IconButton
              id="btn-send-comment"
              iconProps={{ iconName: "Send", className: classes.iconStyles }}
              disabled={
                props.readonly || commentText.trim() === "" || isLoading
              }
              onClick={onSubmit}
              styles={{
                root: { height: 42, width: 42, margin: "6px 8px" }
              }}
            />
          </div>
        )}
        {errorMessage && (
          <MessageBar messageBarType={MessageBarType.error}>
            {errorMessage}
          </MessageBar>
        )}
      </div>

      {showModal && (
        <div className="modal-bg">
          <div className="modal-right" style={{ width: 400 }}>
            <form>
              <div className={classes.header}>Delete Comment</div>
              <div className={classes.body}>
                <span>
                  You are about to delete this comment. Do you want to proceed?
                </span>
              </div>
                          <div className={classes.footer}>
                              <PrimaryButton
                                  id="btn-submit-undo"
                                  type="submit" 
                                  onClick={deleteComment}
                                  disabled={isLoading}
                                  text="Proceed"
                              />
                {/*<button*/}
                {/*  id="btn-submit-undo"*/}
                {/*  className={classes.primaryButton}*/}
                {/*  type="submit"*/}
                {/*  onClick={deleteComment}*/}
                {/*  disabled={isLoading}*/}
                {/*>*/}
                {/*  Proceed*/}
                {/*</button>*/}
                              &nbsp;
                              <DefaultButton
                                  id="btn-cancel-undo"
                                  onClick={() => setShowModal(false)}
                                  disabled={isLoading}
                                  text="Cancel"
                              />
                {/*<button*/}
                {/*  id="btn-cancel-undo"*/}
                {/*  className={classes.modalButton}*/}
                {/*  type="button"*/}
                {/*  onClick={() => setShowModal(false)}*/}
                {/*  disabled={isLoading}*/}
                {/*>*/}
                {/*  Cancel*/}
                {/*</button>*/}
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default RemarksPane;
